import { useState, useEffect } from 'react';
import { Input, Select, Button, Space, Alert } from 'antd';
import { CloseOutlined, MenuOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const { Option } = Select;

const FunnelSettingsTab = ({ funnelStatuses = [], addVacancyFunnelStatus, updateVacancyFunnelStatus, currentConfig = null, vacancyId, vacancyStatus }) => {
    const [stages, setStages] = useState(() => {
        if (currentConfig && currentConfig.length > 0) {
            return currentConfig.map(status => ({
                key: status.status_id.toString(),
                name: funnelStatuses.find(s => s.id === status.status_id)?.name || `Статус ${status.status_id}`
            }));
        }
        if (funnelStatuses.length >= 2) {
            const firstStatus = funnelStatuses[0];
            const lastStatus = funnelStatuses[funnelStatuses.length - 1];
            return [
                { key: firstStatus.id.toString(), name: firstStatus.name },
                { key: lastStatus.id.toString(), name: lastStatus.name }
            ];
        }
        return [];
    });

    const [newStageKey, setNewStageKey] = useState(null);
    const [originalStages, setOriginalStages] = useState([]);

    // Проверяем, можно ли редактировать воронку (только для статуса "new")
    const canEditFunnel = vacancyStatus === 'new';

    useEffect(() => {
        if (currentConfig && currentConfig.length > 0) {
            const newStages = currentConfig.map(status => ({
                key: status.status_id.toString(),
                name: funnelStatuses.find(s => s.id === status.status_id)?.name || `Статус ${status.status_id}`
            }));
            setStages(newStages);
            setOriginalStages([...newStages]);
        } else {
            if (funnelStatuses.length >= 2) {
                const firstStatus = funnelStatuses[0];
                const lastStatus = funnelStatuses[funnelStatuses.length - 1];
                const defaultStages = [
                    { key: firstStatus.id.toString(), name: firstStatus.name },
                    { key: lastStatus.id.toString(), name: lastStatus.name }
                ];
                setStages(defaultStages);
                setOriginalStages([...defaultStages]);
            } else {
                setStages([]);
                setOriginalStages([]);
            }
        }
    }, [currentConfig, funnelStatuses]);

    const getFirstAndLastStatuses = () => {
        if (funnelStatuses.length >= 2) {
            const firstStatus = funnelStatuses[0];
            const lastStatus = funnelStatuses[funnelStatuses.length - 1];
            return { firstStatus, lastStatus };
        }
        return { firstStatus: null, lastStatus: null };
    };

    const hasChanges = () => {
        const { firstStatus, lastStatus } = getFirstAndLastStatuses();

        if (stages.length !== originalStages.length) return true;

        if (!currentConfig || currentConfig.length === 0) {
            if (firstStatus && lastStatus) {
                return stages.length === 2 &&
                    stages[0].key === firstStatus.id.toString() &&
                    stages[1].key === lastStatus.id.toString();
            }

            return false;
        }

        for (let i = 0; i < stages.length; i++) {
            if (stages[i].key !== originalStages[i].key) {
                return true;
            }
        }
        return false;
    };

    const isFunnelEmpty = () => {
        // Не показываем сообщение, если есть сохраненная конфигурация
        if (currentConfig && currentConfig.length > 0) {
            return false;
        }

        // Не показываем сообщение, если воронка содержит больше 2 статусов
        if (stages.length > 2) {
            return false;
        }

        const { firstStatus, lastStatus } = getFirstAndLastStatuses();

        if (firstStatus && lastStatus) {
            return stages.length === 2 &&
                stages[0].key === firstStatus.id.toString() &&
                stages[1].key === lastStatus.id.toString();
        }
        return false;
    };

    const availableStages = funnelStatuses.filter(status =>
        !stages.find(stage => stage.key === status.id.toString())
    );



    const handleRemove = (key) => {
        if (!canEditFunnel) return;
        const { firstStatus, lastStatus } = getFirstAndLastStatuses();
        if ((firstStatus && key === firstStatus.id.toString()) ||
            (lastStatus && key === lastStatus.id.toString())) return;
        setStages(prev => prev.filter(stage => stage.key !== key));
    };

    const handleAddStage = () => {
        if (!canEditFunnel) return;
        const found = availableStages.find(s => s.id.toString() === newStageKey);
        if (found && !stages.find(s => s.key === found.id.toString())) {
            setStages(prev => {
                const copy = [...prev];
                const { lastStatus } = getFirstAndLastStatuses();
                const finishIndex = lastStatus ? copy.findIndex(stage => stage.key === lastStatus.id.toString()) : -1;
                const insertIndex = finishIndex > -1 ? finishIndex : copy.length;
                copy.splice(insertIndex, 0, {
                    key: found.id.toString(),
                    name: found.name
                });
                return copy;
            });
            setNewStageKey(null);
        }
    };

    const handleDragEnd = (result) => {
        if (!result.destination || !canEditFunnel) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        const sourceStage = stages[sourceIndex];
        const destStage = stages[destinationIndex];
        const { firstStatus, lastStatus } = getFirstAndLastStatuses();

        if ((firstStatus && sourceStage.key === firstStatus.id.toString()) ||
            (lastStatus && sourceStage.key === lastStatus.id.toString())) return;
        if ((firstStatus && destStage.key === firstStatus.id.toString()) ||
            (lastStatus && destStage.key === lastStatus.id.toString())) return;

        const updated = [...stages];
        const [moved] = updated.splice(sourceIndex, 1);
        updated.splice(destinationIndex, 0, moved);
        setStages(updated);
    };

    const handleSaveConfiguration = async () => {
        if (!canEditFunnel) return;

        const configuration = stages.map((stage, index) => ({
            status_id: parseInt(stage.key),
        }));

        if (currentConfig && currentConfig.length > 0) {
            await updateVacancyFunnelStatus(configuration);
        } else {
            await addVacancyFunnelStatus(configuration);
        }

        setOriginalStages([...stages]);
    };

    return (
        <div>
            {!canEditFunnel && (
                <Alert
                    message="Редактирование воронки ограничено"
                    description="Настройка воронки доступна только для вакансий со статусом 'Новая'"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {isFunnelEmpty() && (
                <div style={{
                    padding: 16,
                    textAlign: 'center',
                    color: '#999',
                    fontStyle: 'italic',
                    marginBottom: 16,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 4
                }}>
                    Воронка пуста. Добавьте этапы для настройки воронки.
                </div>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="funnelStages">
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{ padding: 8, background: '#f9f9f9', borderRadius: 4 }}
                        >
                            {stages.map((stage, index) => {
                                const { firstStatus, lastStatus } = getFirstAndLastStatuses();
                                const isFirstOrLast = (firstStatus && stage.key === firstStatus.id.toString()) ||
                                    (lastStatus && stage.key === lastStatus.id.toString());

                                return (
                                    <Draggable
                                        key={stage.key}
                                        draggableId={stage.key}
                                        index={index}
                                        isDragDisabled={isFirstOrLast}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                style={{
                                                    padding: 8,
                                                    marginBottom: 8,
                                                    border: '1px solid #ddd',
                                                    borderRadius: 4,
                                                    backgroundColor: snapshot.isDragging ? '#e6f7ff' : '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    ...provided.draggableProps.style
                                                }}
                                            >
                                                <Space>
                                                    {!isFirstOrLast && canEditFunnel && (
                                                        <CloseOutlined
                                                            onClick={() => handleRemove(stage.key)}
                                                            style={{ color: 'red', cursor: 'pointer' }}
                                                        />
                                                    )}
                                                    <Input
                                                        value={stage.name}
                                                        style={{ width: 250 }}
                                                        disabled={true}
                                                    />
                                                </Space>
                                                {!isFirstOrLast && canEditFunnel && (
                                                    <div {...provided.dragHandleProps}>
                                                        <MenuOutlined style={{ cursor: 'grab', color: '#888' }} />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <div style={{ display: 'flex', alignItems: 'center', marginTop: 16, gap: 8 }}>
                <Select
                    placeholder="Добавить этап"
                    value={newStageKey}
                    onChange={value => setNewStageKey(value)}
                    style={{ width: 200 }}
                    disabled={!canEditFunnel}
                >
                    {availableStages.map(stage => (
                        <Option key={stage.id} value={stage.id.toString()}>
                            {stage.name}
                        </Option>
                    ))}
                </Select>
                <Button
                    onClick={handleAddStage}
                    type="primary"
                    disabled={!canEditFunnel}
                >
                    Добавить
                </Button>
                <Button
                    onClick={handleSaveConfiguration}
                    type="default"
                    disabled={!hasChanges() || !canEditFunnel}
                >
                    Сохранить конфигурацию
                </Button>
            </div>
        </div>
    );
};

export default FunnelSettingsTab;